// @ts-nocheck
"use client";
import { useEffect, useRef } from "react";

/**
 * FlowCanvas — chaotic streams that gradually orchestrate into aligned bezier paths.
 * Loops every ~14s. Settles into a vertical aligned state.
 */
function FlowCanvas({ glow = 0.6, density = 2, autoOrbit = true, palette = "mono", style = "realistic", theme = "blueDark" }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const stateRef = useRef({ raf: 0, t0: 0, particles: [], w: 0, h: 0, dpr: 1, mouse: { x: -9999, y: -9999 } });

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");

    // Particle color depends on palette + theme
    let accent;
    if (palette === "cyan") {
      accent = { r: 120, g: 220, b: 255 };
    } else if (theme === "light") {
      accent = { r: 30, g: 50, b: 90 };
    } else if (theme === "blueDark") {
      accent = { r: 226, g: 232, b: 240 };
    } else {
      accent = { r: 237, g: 236, b: 227 };
    }
    // Orb color also adjusts (warm vs cool)
    const orbColor = theme === "light" ? "37,99,235" : "120,210,255";
    const orbHi = theme === "light" ? "180,200,240" : "200,235,255";
    const orbMid = theme === "light" ? "37,99,235" : "120,210,255";
    const orbLow = theme === "light" ? "30,58,138" : "40,90,140";
    const orbRing = theme === "light" ? "37,99,235" : "155,214,255";

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const s = stateRef.current;
      s.w = w; s.h = h; s.dpr = dpr;
      seed();
    };

    const COUNT = Math.round(140 * density);

    const seed = () => {
      const s = stateRef.current;
      s.particles = [];
      for (let i = 0; i < COUNT; i++) {
        // Each particle has a "lane" — its target orchestrated y position.
        const lane = Math.floor((i / COUNT) * 11); // 11 lanes (staff-line metaphor)
        const laneY = (s.h * 0.18) + (lane / 10) * (s.h * 0.64);
        s.particles.push({
          x: Math.random() * s.w,
          y: Math.random() * s.h,
          vx: (Math.random() - 0.5) * 1.2,
          vy: (Math.random() - 0.5) * 1.2,
          laneY,
          phase: Math.random() * Math.PI * 2,
          speed: 0.4 + Math.random() * 0.6,
          size: 1 + Math.random() * 1.6,
          life: Math.random() * 200 + 150,
          age: Math.random() * 200,
        });
      }
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      stateRef.current.mouse.x = e.clientX - r.left;
      stateRef.current.mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      stateRef.current.mouse.x = -9999;
      stateRef.current.mouse.y = -9999;
    };

    resize();
    window.addEventListener("resize", resize);
    wrap.addEventListener("mousemove", onMove);
    wrap.addEventListener("mouseleave", onLeave);

    stateRef.current.t0 = performance.now();

    const draw = () => {
      const s = stateRef.current;
      const now = performance.now();
      const elapsed = (now - s.t0) / 1000; // seconds
      const cycle = 14; // seconds per loop
      const tCycle = (elapsed % cycle) / cycle; // 0..1

      // Orchestration progress: 0 = chaos, 1 = aligned. Eases in and back.
      // phases: 0..0.25 chaos, 0.25..0.55 transition, 0.55..0.85 aligned, 0.85..1 dissolve
      let order = 0;
      if (tCycle < 0.25) order = 0;
      else if (tCycle < 0.55) order = (tCycle - 0.25) / 0.30;
      else if (tCycle < 0.85) order = 1;
      else order = 1 - (tCycle - 0.85) / 0.15;
      order = Math.max(0, Math.min(1, order));
      const ease = order < 0.5 ? 2 * order * order : 1 - Math.pow(-2 * order + 2, 2) / 2;

      // Fade trails — match theme background so erasing reveals correct base
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = `rgba(0,0,0,${0.06 + 0.04 * (1 - ease)})`;
      ctx.fillRect(0, 0, s.w, s.h);
      ctx.globalCompositeOperation = "source-over";

      // Draw connecting bezier "staff lines" that strengthen with order
      if (ease > 0.05) {
        const lines = 5;
        for (let li = 0; li < lines; li++) {
          const y = s.h * 0.22 + (li / (lines - 1)) * s.h * 0.56;
          const wob = 12 * (1 - ease) + 3 * Math.sin(elapsed * 0.7 + li);
          ctx.beginPath();
          ctx.moveTo(0, y + wob);
          ctx.bezierCurveTo(s.w * 0.33, y - wob, s.w * 0.66, y + wob, s.w, y - wob);
          ctx.strokeStyle = `rgba(${accent.r},${accent.g},${accent.b},${0.04 + 0.10 * ease * glow})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      const orbit = autoOrbit ? Math.sin(elapsed * 0.15) * 0.5 : 0;

      for (const p of s.particles) {
        // Chaotic flow field
        const ang = (Math.cos(p.x * 0.004 + elapsed * 0.2) + Math.sin(p.y * 0.004 + p.phase)) * Math.PI;
        const chaosVx = Math.cos(ang) * 0.45 * p.speed;
        const chaosVy = Math.sin(ang) * 0.45 * p.speed;

        // Orchestrated flow: move right along lane, gently sine-wave the y
        const orchVx = 1.2 * p.speed;
        const targetY = p.laneY + Math.sin(elapsed * 0.8 + p.x * 0.01) * 6;
        const orchVy = (targetY - p.y) * 0.08;

        const vx = chaosVx * (1 - ease) + orchVx * ease;
        const vy = chaosVy * (1 - ease) + orchVy * ease + orbit * 0.2;

        // Mouse repulsion
        const dx = s.mouse.x - p.x;
        const dy = s.mouse.y - p.y;
        const d2 = dx * dx + dy * dy;
        let rx = 0, ry = 0;
        if (d2 < 140 * 140) {
          const d = Math.sqrt(d2) || 1;
          const f = (140 - d) / 140;
          rx = -dx / d * f * 1.6;
          ry = -dy / d * f * 1.6;
        }

        p.x += vx + rx;
        p.y += vy + ry;
        p.age++;

        // Wrap
        if (p.x < -10) p.x = s.w + 10;
        if (p.x > s.w + 10) p.x = -10;
        if (p.y < -10) p.y = s.h + 10;
        if (p.y > s.h + 10) p.y = -10;

        // Draw
        const lifeT = (p.age % p.life) / p.life;
        const fade = 1 - Math.abs(lifeT - 0.5) * 1.6;
        const alpha = (0.18 + 0.55 * fade) * (0.65 + 0.35 * glow);
        const sz = p.size * (style === "realistic" ? 1 : 0.8);
        ctx.fillStyle = `rgba(${accent.r},${accent.g},${accent.b},${alpha})`;
        ctx.fillRect(p.x, p.y, sz, sz);

        // In aligned phase, draw short trail strokes (notes on a staff)
        if (ease > 0.6) {
          ctx.strokeStyle = `rgba(${accent.r},${accent.g},${accent.b},${0.05 + 0.18 * ease * glow})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(p.x - 6 * ease, p.y);
          ctx.lineTo(p.x + 8 * ease, p.y);
          ctx.stroke();
        }
      }

      // Subtle vignette glow at center bottom — where logo sits
      if (glow > 0) {
        const grad = ctx.createRadialGradient(s.w * 0.5, s.h * 0.55, 0, s.w * 0.5, s.h * 0.55, Math.min(s.w, s.h) * 0.55);
        grad.addColorStop(0, `rgba(${accent.r},${accent.g},${accent.b},${0.06 * glow * ease})`);
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, s.w, s.h);
      }

      // (Orb replaced by full Orkestria logo overlay rendered in hero.jsx — see .ork-hero-mark)
      // We still draw a soft radial aura behind where the logo will sit, themed.
      const orbFadeIn = Math.min(1, Math.max(0, (elapsed - 0.6) / 2.0));
      const orbCycle = 0.55 + 0.45 * Math.sin(elapsed * 0.4);
      const orbOpacity = orbFadeIn * (0.45 + 0.20 * orbCycle);
      const orbR = Math.min(s.w, s.h) * 0.18;
      const orbCx = s.w * 0.5;
      const orbCy = s.h * 0.46;
      const aura = ctx.createRadialGradient(orbCx, orbCy, 0, orbCx, orbCy, orbR * 2.4);
      aura.addColorStop(0, `rgba(${orbColor},${0.08 * glow * orbOpacity})`);
      aura.addColorStop(0.35, `rgba(${orbColor},${0.04 * glow * orbOpacity})`);
      aura.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = aura;
      ctx.beginPath();
      ctx.arc(orbCx, orbCy, orbR * 2.4, 0, Math.PI * 2);
      ctx.fill();

      stateRef.current.raf = requestAnimationFrame(draw);
    };
    stateRef.current.raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(stateRef.current.raf);
      window.removeEventListener("resize", resize);
      wrap.removeEventListener("mousemove", onMove);
      wrap.removeEventListener("mouseleave", onLeave);
    };
  }, [glow, density, autoOrbit, palette, style, theme]);

  return (
    <div ref={wrapRef} style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }}></canvas>
    </div>
  );
}

export { FlowCanvas };
export default FlowCanvas;
